import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { getApiUrl } from './config'

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable
  }
}

interface PaymentInvoicePDFOptions {
  payment: {
    id: string
    transactionId?: string
    amount: number
    paymentMethod: string
    paymentType: string
    status: string
    paidAt?: string
    createdAt: string
    notes?: string
    reservation?: {
      reservationNumber: string
      checkInDate: string
      checkOutDate: string
      room?: {
        roomNumber: string
        currency?: string
      }
    }
    guest?: {
      firstName: string
      lastName: string
      email?: string
      phone?: string
      address?: string
    }
  }
  folio?: {
    totalAmount: number
    balance: number
    items?: Array<{
      name: string
      description?: string
      price: number
      quantity: number
      total: number
      category?: string
    }>
  }
  company: {
    name: string
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
    address?: string
    city?: string
    state?: string
    postalCode?: string
    email?: string
    phone?: string
    website?: string
    taxId?: string
    invoiceTerms?: string
    invoiceFooter?: string
  }
}

export class PaymentInvoicePDFGenerator {
  private doc: jsPDF
  private primaryColor: string
  private secondaryColor: string
  private margin = 20
  private currentY = 20
  private readonly lineHeight = 5
  private readonly sectionSpacing = 12
  private readonly subSectionSpacing = 8

  constructor(private options: PaymentInvoicePDFOptions) {
    this.doc = new jsPDF('p', 'mm', 'a4')
    // Use company colors, default to gold/bronze theme
    this.primaryColor = options.company.primaryColor || '#c29f72' // Gold/Bronze
    this.secondaryColor = options.company.secondaryColor || '#1565c0' // Blue
  }

  private getCurrency(): string {
    return this.options.payment.reservation?.room?.currency || 'FRW';
  }

  private formatCurrency(amount: number, currency?: string): string {
    const curr = currency || this.getCurrency();
    if (curr === 'Frw' || curr === 'FRW' || curr === 'RWF') {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw`
    }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: curr
      }).format(amount)
    } catch {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${curr}`
    }
  }

  private setColor(color: string) {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    this.doc.setTextColor(r, g, b)
  }

  private resetColor() {
    this.doc.setTextColor(0, 0, 0)
  }

  private addLine(y?: number) {
    const lineY = y || this.currentY
    this.doc.setDrawColor(200, 200, 200)
    this.doc.line(this.margin, lineY, 190, lineY)
  }

  private hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '')
    const r = parseInt(h.substr(0, 2), 16)
    const g = parseInt(h.substr(2, 2), 16)
    const b = parseInt(h.substr(4, 2), 16)
    return [r, g, b]
  }

  private async addHeader() {
    const { company, payment } = this.options
    
    // Professional logo and company name
    let logoAdded = false
    const logoSize = 20
    const logoY = this.currentY

    // Try to load company logo with proper URL conversion
    let logoToUse = company.logoUrl || '/brand.svg'
    if (logoToUse && !logoToUse.startsWith('http://') && !logoToUse.startsWith('https://')) {
      // Use getApiUrl to get the correct backend URL (works in both dev and production)
      logoToUse = getApiUrl(logoToUse.startsWith('/') ? logoToUse.substring(1) : logoToUse)
    }
    
    if (logoToUse) {
      try {
        // Load image properly using Image object (same as hotel report)
        const img = new Image()
        img.crossOrigin = 'anonymous'
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(true)
          img.onerror = (e) => reject(e)
          img.src = logoToUse
        })
        this.doc.addImage(img, 'PNG', this.margin, logoY, logoSize, logoSize)
        logoAdded = true
      } catch (error) {
        // Logo failed to load, continue without it
        logoAdded = false
      }
    }

    // Company name aligned with logo center
    const companyNameY = logoY + (logoSize / 2) + 2
    const companyX = logoAdded ? this.margin + logoSize + 4 : this.margin
    
    this.doc.setFontSize(18)
    this.doc.setTextColor(0, 0, 0)
    this.doc.setFont(undefined, 'bold')
    this.doc.text(company.name, companyX, companyNameY)
    this.doc.setFont(undefined, 'normal')
    
    // Company website on separate line below name
    let websiteY = companyNameY + 5
    if (company.website) {
      this.doc.setFontSize(9)
      this.doc.setTextColor(100, 100, 100)
      this.doc.text(company.website, companyX, websiteY)
      websiteY += 5
    }

    // Company details with consistent spacing
    this.resetColor()
    this.doc.setFontSize(9)
    let detailsY = company.website ? websiteY : companyNameY + 5
    const detailsX = logoAdded ? this.margin + logoSize + 4 : this.margin
    
    if (company.address) {
      this.doc.text(company.address, detailsX, detailsY)
      detailsY += this.lineHeight
    }
    
    if (company.city || company.state || company.postalCode) {
      const location = [company.city, company.state, company.postalCode].filter(Boolean).join(', ')
      this.doc.text(location, detailsX, detailsY)
      detailsY += this.lineHeight
    }
    
    if (company.email) {
      this.doc.text(`Email: ${company.email}`, detailsX, detailsY)
      detailsY += this.lineHeight
    }
    
    if (company.phone) {
      this.doc.text(`Phone: ${company.phone}`, detailsX, detailsY)
      detailsY += this.lineHeight
    }

    // Payment Invoice header on the right - using black color
    this.doc.setFontSize(18)
    this.doc.setTextColor(0, 0, 0) // Black color
    this.doc.setFont(undefined, 'bold')
    const invoiceText = 'PAYMENT INVOICE'
    const invoiceX = 190 - this.doc.getTextWidth(invoiceText)
    this.doc.text(invoiceText, invoiceX, logoY + 8)
    
    // Transaction ID
    this.resetColor()
    this.doc.setFontSize(14)
    this.doc.setFont(undefined, 'normal')
    const transactionId = payment.transactionId || payment.id
    const transactionText = `#${transactionId}`
    const transactionX = 190 - this.doc.getTextWidth(transactionText)
    this.doc.text(transactionText, transactionX, logoY + 17)
    
    // Status badge with proper padding
    this.doc.setFontSize(9)
    const statusText = payment.status.toUpperCase()
    const statusWidth = this.doc.getTextWidth(statusText) + 6
    const statusHeight = 6
    const statusY = logoY + 21
    
    // Status background with black color
    this.doc.setFillColor(0, 0, 0) // Black background
    this.doc.roundedRect(190 - statusWidth, statusY, statusWidth, statusHeight, 1, 1, 'F')
    
    this.doc.setTextColor(255, 255, 255)
    this.doc.setFont(undefined, 'bold')
    this.doc.text(statusText, 190 - statusWidth + 3, statusY + 4.2)
    this.resetColor()
    this.doc.setFont(undefined, 'normal')

    // Professional spacing before line
    this.currentY = Math.max(detailsY, logoY + 32)
    this.addLine(this.currentY)
    this.currentY += this.subSectionSpacing
  }

  private addGuestInfo() {
    const { payment } = this.options
    if (!payment.guest) return

    // Bill To Section
    this.doc.setFontSize(11)
    this.doc.setFont(undefined, 'bold')
    this.doc.setTextColor(0, 0, 0) // Black color
    this.doc.text('BILL TO:', this.margin, this.currentY)
    
    this.doc.setFont(undefined, 'normal')
    this.doc.setFontSize(10)
    let billToY = this.currentY + 6
    
    const guestName = `${payment.guest.firstName} ${payment.guest.lastName}`
    this.doc.setFont(undefined, 'bold')
    this.doc.text(guestName, this.margin, billToY)
    this.doc.setFont(undefined, 'normal')
    billToY += this.lineHeight + 1
    
    if (payment.guest.email) {
      this.doc.setFontSize(9)
      this.doc.text(payment.guest.email, this.margin, billToY)
      billToY += this.lineHeight
    }
    
    if (payment.guest.phone) {
      this.doc.setFontSize(9)
      this.doc.text(payment.guest.phone, this.margin, billToY)
      billToY += this.lineHeight
    }
    
    if (payment.guest.address) {
      this.doc.setFontSize(9)
      this.doc.text(payment.guest.address, this.margin, billToY)
      billToY += this.lineHeight
    }

    // Payment Details Section on the right
    const detailsX = 110
    this.doc.setFont(undefined, 'bold')
    this.doc.setFontSize(11)
    this.doc.text('PAYMENT DETAILS', detailsX, this.currentY)
    
    this.doc.setFont(undefined, 'normal')
    this.doc.setFontSize(9)
    let detailsY = this.currentY + 6
    
    const paymentDate = payment.paidAt 
      ? new Date(payment.paidAt).toLocaleDateString()
      : new Date(payment.createdAt).toLocaleDateString()
    
    const labelX = detailsX
    const valueX = detailsX + 32
    
    this.doc.setTextColor(80, 80, 80)
    this.doc.text('Date:', labelX, detailsY)
    this.doc.setTextColor(0, 0, 0)
    this.doc.text(paymentDate, valueX, detailsY)
    detailsY += this.lineHeight
    
    this.doc.setTextColor(80, 80, 80)
    this.doc.text('Method:', labelX, detailsY)
    this.doc.setTextColor(0, 0, 0)
    this.doc.text(payment.paymentMethod.toUpperCase(), valueX, detailsY)
    detailsY += this.lineHeight
    
    this.doc.setTextColor(80, 80, 80)
    this.doc.text('Type:', labelX, detailsY)
    this.doc.setTextColor(0, 0, 0)
    this.doc.text(payment.paymentType.replace('_', ' ').toUpperCase(), valueX, detailsY)
    detailsY += this.lineHeight
    
    this.doc.setTextColor(80, 80, 80)
    this.doc.text('Status:', labelX, detailsY)
    this.doc.setTextColor(0, 0, 0)
    this.doc.text(payment.status, valueX, detailsY)
    detailsY += this.lineHeight

    // Reservation Details if available
    if (payment.reservation) {
      detailsY += 4
      this.doc.setFont(undefined, 'bold')
      this.doc.setFontSize(11)
      this.doc.text('RESERVATION', detailsX, detailsY)
      this.doc.setFont(undefined, 'normal')
      this.doc.setFontSize(9)
      detailsY += 6
      
      this.doc.text(`#${payment.reservation.reservationNumber}`, detailsX, detailsY)
      detailsY += this.lineHeight
      
      if (payment.reservation.room) {
        this.doc.text(`Room ${payment.reservation.room.roomNumber}`, detailsX, detailsY)
        detailsY += this.lineHeight
      }
      
      const checkIn = new Date(payment.reservation.checkInDate).toLocaleDateString()
      const checkOut = new Date(payment.reservation.checkOutDate).toLocaleDateString()
      
      this.doc.setTextColor(80, 80, 80)
      this.doc.text('Check-in:', detailsX, detailsY)
      this.doc.setTextColor(0, 0, 0)
      this.doc.text(checkIn, detailsX + 25, detailsY)
      detailsY += this.lineHeight
      
      this.doc.setTextColor(80, 80, 80)
      this.doc.text('Check-out:', detailsX, detailsY)
      this.doc.setTextColor(0, 0, 0)
      this.doc.text(checkOut, detailsX + 25, detailsY)
    }

    this.resetColor()
    this.currentY = Math.max(billToY, detailsY) + this.sectionSpacing
  }

  private addFolioItems() {
    const { folio } = this.options
    if (!folio || !folio.items || folio.items.length === 0) return

    // Table headers
    const headers = [['Description', 'Qty', 'Rate', 'Amount']]
    
    // Table data from folio items with formatted quantity
    const data = folio.items.map(item => {
      const qty = Number(item.quantity);
      // Format quantity: show whole number if it's an integer, otherwise max 2 decimals
      const formattedQty = Number.isInteger(qty) ? qty.toString() : qty.toFixed(2);
      
      // Detect currency from POS items
      let itemCurrency = this.getCurrency();
      if (item.name?.includes('POS Invoice') && item.description) {
        const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
        if (currencyMatch) {
          itemCurrency = currencyMatch[1];
        }
      }
      
      return [
        item.description || item.name,
        formattedQty,
        this.formatCurrency(Number(item.price), itemCurrency),
        this.formatCurrency(Number(item.total), itemCurrency)
      ];
    })

    // Generate table with black header color and professional spacing
    autoTable(this.doc, {
      head: headers,
      body: data,
      startY: this.currentY,
      margin: { left: this.margin, right: this.margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        lineColor: [220, 220, 220],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [0, 0, 0], // Black header
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' }
      }
    })

    this.currentY = (this.doc as any).lastAutoTable.finalY + this.subSectionSpacing
  }

  private addPaymentDetails() {
    const { payment, folio } = this.options

    // If no folio items, show payment line with professional spacing
    if (!folio || !folio.items || folio.items.length === 0) {
      const tableData = [
        ['Description', 'Amount'],
        [
          `${payment.paymentType.replace('_', ' ').toUpperCase()} - ${payment.paymentMethod.toUpperCase()}`,
          this.formatCurrency(Number(payment.amount))
        ]
      ]

      autoTable(this.doc, {
        startY: this.currentY,
        head: [tableData[0]],
        body: [tableData[1]],
        theme: 'striped',
        headStyles: {
          fillColor: [0, 0, 0], // Black header
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          cellPadding: 4
        },
        bodyStyles: {
          fontSize: 10,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 130 },
          1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: this.margin, right: this.margin }
      })

      this.currentY = (this.doc as any).lastAutoTable.finalY + this.subSectionSpacing
    }

    // Professional totals section with accurate spacing
    const totalsX = 130
    const totalsY = this.currentY
    const totalAmount = folio ? Number(folio.totalAmount) : Number(payment.amount)

    // Total amount box with white background and black border for better visibility
    this.doc.setFillColor(255, 255, 255) // White background
    this.doc.setDrawColor(0, 0, 0) // Black border
    this.doc.setLineWidth(0.5)
    this.doc.roundedRect(totalsX, totalsY, 60, 14, 2, 2, 'FD')
    
    this.doc.setFontSize(10)
    this.doc.setFont(undefined, 'bold')
    this.doc.setTextColor(0, 0, 0)
    this.doc.text('Total Amount:', totalsX + 3, totalsY + 5.5)
    this.doc.setFontSize(13)
    this.doc.text(this.formatCurrency(totalAmount), totalsX + 3, totalsY + 11)
    this.resetColor()
    this.doc.setFont(undefined, 'normal')
    this.doc.setLineWidth(0.2) // Reset line width

    this.currentY = totalsY + 18
  }

  private addNotes() {
    const { payment, company } = this.options

    if (payment.notes || company.invoiceTerms) {
      this.currentY += this.subSectionSpacing
      this.addLine()
      this.currentY += this.subSectionSpacing

      this.doc.setFontSize(11)
      this.doc.setFont(undefined, 'bold')
      this.doc.text('Notes:', this.margin, this.currentY)
      this.doc.setFont(undefined, 'normal')
      this.currentY += 6

      if (payment.notes) {
        this.doc.setFontSize(9)
        const notesLines = this.doc.splitTextToSize(payment.notes, 170)
        this.doc.text(notesLines, this.margin, this.currentY)
        this.currentY += (notesLines.length * this.lineHeight)
      }

      if (company.invoiceTerms) {
        this.currentY += 4
        this.doc.setFontSize(9)
        this.doc.setTextColor(60, 60, 60)
        const termsLines = this.doc.splitTextToSize(company.invoiceTerms, 170)
        this.doc.text(termsLines, this.margin, this.currentY)
        this.currentY += (termsLines.length * this.lineHeight)
        this.resetColor()
      }

      // Minimal padding after notes - just enough space before footer
      this.currentY += 2
    }
  }

  private addFooter() {
    const { company } = this.options
    const pageHeight = this.doc.internal.pageSize.height
    const footerY = pageHeight - 22

    this.addLine(footerY - 8)

    this.doc.setFontSize(8)
    this.doc.setTextColor(100, 100, 100)
    this.doc.text(`Generated on ${new Date().toLocaleString()} • Created via Ikoro`, this.margin, footerY)

    if (company.invoiceFooter) {
      this.doc.setFontSize(9)
      this.doc.setTextColor(60, 60, 60)
      const footerLines = this.doc.splitTextToSize(company.invoiceFooter, 170)
      this.doc.text(footerLines, this.margin, footerY + 5)
    }

    this.resetColor()
  }

  async generate(): Promise<Blob> {
    await this.addHeader()
    this.addGuestInfo()
    this.addFolioItems()
    this.addPaymentDetails()
    this.addNotes()
    this.addFooter()

    return this.doc.output('blob')
  }

  async download(filename?: string) {
    const blob = await this.generate()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `payment-${this.options.payment.transactionId || this.options.payment.id}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}